import * as THREE from 'three';
import { C } from '../game/constants';
import { World } from '../game/World';

/**
 * The on-foot miner: capsule body, visor, jetpack blocks, headlamp.
 * Side-view platformer physics (gravity + jetpack) with AABB-vs-grid
 * collision, plus the aimable mining laser visual.
 */
export class Miner {
  readonly group = new THREE.Group();          // root: position only
  private readonly body = new THREE.Group();   // yaws/leans (3D turn)
  vel = new THREE.Vector2();
  readonly lamp: THREE.PointLight;
  readonly laser: THREE.Mesh;
  private readonly laserMat: THREE.MeshBasicMaterial;
  private readonly visor: THREE.Mesh;
  private readonly jetL: THREE.Mesh;
  private readonly jetR: THREE.Mesh;
  facing = 1;

  constructor() {
    const suitMat = new THREE.MeshLambertMaterial({ color: 0xe8e4da });
    const darkMat = new THREE.MeshLambertMaterial({ color: 0x3c4250 });
    const packMat = new THREE.MeshLambertMaterial({ color: 0x8a94a8 });

    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.36, 0.8, 6, 10), suitMat);
    this.visor = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.22, 0.3), darkMat);
    this.visor.position.set(0.18, 0.52, 0);
    const pack = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.62, 0.34), packMat);
    pack.position.set(-0.32, 0.1, 0);

    const flameMat = new THREE.MeshBasicMaterial({
      color: 0xffa040, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.jetL = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.5, 6), flameMat);
    this.jetL.rotation.x = Math.PI;
    this.jetL.position.set(-0.32, -0.35, 0.12);
    this.jetR = this.jetL.clone();
    this.jetR.position.z = -0.12;
    this.jetL.visible = this.jetR.visible = false;

    this.lamp = new THREE.PointLight(0xffe8c0, 14, 13, 1.6);
    this.lamp.position.set(0.2, 0.5, 1.5);

    this.laserMat = new THREE.MeshBasicMaterial({
      color: 0xff6a40, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.laser = new THREE.Mesh(new THREE.BoxGeometry(1, 0.09, 0.09), this.laserMat);
    this.laser.visible = false;

    this.body.add(body, this.visor, pack, this.jetL, this.jetR);
    // Lamp + laser live on the root so world-space aim math stays valid
    // while the body yaws.
    this.group.add(this.body, this.lamp, this.laser);
  }

  get pos(): THREE.Vector3 { return this.group.position; }

  step(dt: number, moveX: number, jet: boolean, world: World): void {
    this.vel.y += C.miner.gravity * dt;
    if (jet) this.vel.y += C.miner.jetpack * dt;
    this.vel.x += moveX * C.miner.move * dt;
    this.vel.x *= Math.pow(C.miner.drag, dt * 60);
    this.vel.x = THREE.MathUtils.clamp(this.vel.x, -C.miner.maxSpeed, C.miner.maxSpeed);
    this.vel.y = THREE.MathUtils.clamp(this.vel.y, -C.miner.maxSpeed * 2, C.miner.maxSpeed);

    // Axis-separated AABB vs block grid (probe corners at target)
    const r = C.miner.radius, h = C.miner.height;
    let nx = this.pos.x + this.vel.x * dt;
    if (this.collides(nx, this.pos.y, r, h, world)) {
      nx = this.pos.x;
      this.vel.x = 0;
    }
    let ny = this.pos.y + this.vel.y * dt;
    if (this.collides(nx, ny, r, h, world)) {
      ny = this.pos.y;
      this.vel.y = 0;
    }
    this.pos.set(nx, ny, 0);

    if (Math.abs(moveX) > 0.05) this.facing = Math.sign(moveX);
    // 3D turn: the body yaws toward travel (visor/pack swing around in
    // depth), leans into motion, tips back a touch while jetpacking.
    const targetYaw = this.facing > 0 ? 0.55 : Math.PI - 0.55;
    this.body.rotation.y = THREE.MathUtils.lerp(this.body.rotation.y, targetYaw, dt * 8);
    const lean = THREE.MathUtils.clamp(-this.vel.x * 0.03, -0.3, 0.3);
    this.body.rotation.z = THREE.MathUtils.lerp(
      this.body.rotation.z, lean + (jet ? 0.08 * this.facing : 0), dt * 8);
    this.jetL.visible = this.jetR.visible = jet;
    if (jet) {
      const s = 0.8 + Math.random() * 0.5;
      this.jetL.scale.y = this.jetR.scale.y = s;
    }
  }

  private collides(x: number, y: number, r: number, h: number, world: World): boolean {
    return (
      world.solidAtWorld(x - r, y - h) || world.solidAtWorld(x + r, y - h) ||
      world.solidAtWorld(x - r, y + h) || world.solidAtWorld(x + r, y + h) ||
      world.solidAtWorld(x - r, y) || world.solidAtWorld(x + r, y)
    );
  }

  onGround(world: World): boolean {
    return world.solidAtWorld(this.pos.x, this.pos.y - C.miner.height - 0.08);
  }

  /** Stretch the beam from the chest toward the hit point (or full range). */
  showLaser(target: THREE.Vector2 | null, aim: THREE.Vector2): void {
    if (!target) {
      this.laser.visible = false;
      return;
    }
    const ox = this.pos.x, oy = this.pos.y + 0.3;
    const dx = target.x - ox, dy = target.y - oy;
    const len = Math.hypot(dx, dy);
    this.laser.visible = true;
    this.laser.scale.x = len;
    this.laser.position.set((dx / 2) / 1 + 0, 0.3 + dy / 2, 0.1);
    this.laser.rotation.z = Math.atan2(dy, dx);
    this.laserMat.opacity = 0.7 + Math.random() * 0.3;
    void aim;
  }
}
